<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import IconBox from '../IconBox.vue'
import CopyButton from '../CopyButton/index.vue'
import EditElemental from '../EditElemental/index.vue'
import { useElementalListContext } from '../ElementalList/ElementalListContext'
import ElementalMenu from '@/components/ElemetalMenu/index.vue'
import DraggableElement from '@/components/utils/DragAndDrop/DraggableElement.vue'

import { ElementalTypeEnum, ElementalTypeRoutes } from '@/types/elemental'
import type { Elemental } from '@/types/elemental'
import { useSidebarStore } from '@/stores/sidebar'
import { useElementalListsStore } from '@/stores/useElementalListsStore'

import { useTouch } from '@/composables/useTouch'
import ElemetalIconPicker from '@/components/Elemental/ElemetalIconPicker/index.vue'
import { useDragHover } from '@/composables/useDragHover'
import { RecentItemActionEnum } from '@/types/user'

const props = defineProps<{
  elemental: Elemental
  dataIndex?: number
}>()

defineOptions({
  name: 'ElementalItem',
})

const user = useUser()
const isRecentItem = computed(() => props.elemental.recentItemAction)

const { isSmaller } = useBreakpoints(breakpointsTailwind)
const { toggleSidebar, goToFolder } = useSidebarStore()
const { listId: parentListId } = useElementalListContext()

const router = useRouter()
const isOwner = computed(() => user.value?.id === props.elemental.user.id)
const elementalRoute = computed(() => {
  const route = props.elemental.elementalTypeId !== undefined ? ElementalTypeRoutes[props.elemental.elementalTypeId as keyof typeof ElementalTypeRoutes] : null

  if (isRecentItem.value && isRecentItem.value === RecentItemActionEnum.Feed)
    return `/${route}/${props.elemental.id}?view_type=feed`

  if (isRecentItem.value && isRecentItem.value === RecentItemActionEnum.View)
    return `/${route}/${props.elemental.id}`

  if (isOwner.value)
    return (`/${route}/${props.elemental.id}/edit`)
  else
    return (`/${route}/${props.elemental.url}`)
})

const onMobile = isSmaller('sm')

const handlerClick = () => {
  if (props.elemental.isList && !isRecentItem.value) {
    goToFolder(props.elemental)
    return
  }

  if (onMobile)
    toggleSidebar(false)

  router.push(elementalRoute.value)
}

const avatarSize = 20

const optimizedAvatar = computed(() => {
  if (!props.elemental.avatar)
    return '/prompt_avatar.png'

  return optimizeImage(props.elemental.avatar, {
    width: avatarSize * 2, height: avatarSize * 2,
  })
})
const { state } = useElementalListsStore()
const elemetalTypeId = getElementalType(props.elemental.elementalTypeId, props.elemental.isList)

const { onTouchEnd, onTouchMove, onTouchStart } = useTouch({ onTap: handlerClick })

const wrapperRef = ref<HTMLElement | null>(null)

const { isDragHover } = useDragHover({
  node: wrapperRef,
  disabled: !props.elemental.isList,
  timeActions: {
    '>1000': () => {
      handlerClick()
    },
  },
})
</script>

<template>
  <DraggableElement
    is="li" class="list-none" :data="{
      listId: parentListId,
      elemental,
    }" :text-content="elemental.template ?? elemental.title" :data-index="dataIndex"
  >
    <button
      ref="wrapperRef" :class="[$style.wrapper, { [$style.isDragHover]: isDragHover }]" @click="handlerClick"
      @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd"
    >
      <div v-if="optimizedAvatar" class="avatar flex-shrink-0">
        <div class="w-5 rounded-1 overflow-hidden">
          <img
            :src="optimizedAvatar"
            :width="avatarSize"
            :title="`${elemental.title} avatar`"
            :alt="`${elemental.title} avatar`"
            :height="avatarSize"
          >
        </div>
      </div>
      <IconBox v-else>
        <ElemetalIconPicker :type="elemetalTypeId" />
      </IconBox>

      <UtilsHighLightText text-sm text-soft-black line-clamp-1 text-start :text="elemental.title" :search="state.query" />

      <div :class="$style.actions">
        <nav flex gap-.5 items-center :class="$style.actions__hidden" bg-transparent px-1>
          <CopyButton
            v-if="elemetalTypeId !== ElementalTypeEnum.Table" :elemental="elemental"
            :content="elemental.template || ''"
          />
          <EditElemental v-if="isOwner" :elemental="elemental" />
          <ElementalMenu
            :elemental="elemental"
            :hidde-copy-link-action="elemetalTypeId === ElementalTypeEnum.Table"
            :parent-list-id="parentListId"
          />

          <slot name="actions" />
        </nav>
        <slot name="aditional-actions" />
      </div>
    </button>
  </DraggableElement>
</template>

<style lang="scss" module>
.wrapper {
  .isDragHover {
    @apply bg-grayLighter;
  }

  @apply px-2 rounded;

  &[data-expanded='true']:hover {
    @apply bg-transparent;
  }

  @apply w-auto w-full flex text-darkGray flex-nowrap justify-left py-2 gap-2 items-center relative;

  &:focus-within,
  &:hover {
    @apply bg-grayLighter;

    .actions {
      .actions__hidden {
        @apply flex;
      }
    }
  }

  .actions {
    @apply flex gap-.5 justify-self-end ml-auto absolute right-0 z-10 px-2 items-center;
  }

  .actions__hidden {
    @apply hidden items-center;
  }
}
</style>
